<template>
  <modal-vue
    @close="handleToggleModal"
    :modalActive="modals[modalName]"
    max-width="!w-[1140px] !max-w-[1140px]"
    min-height="90vh"
    max-height="90vh"
    class="figma-custom-modal"
    :has-header-border="true"
  >
    <template #header>
      <div>
        <p class="font-bold text-lg text-center text-textPrimary">Vínculos de políticas</p>
        <p class="text-sm text-center text-gray-600 capitalize">
          Em {{ props.resource.name.toLocaleLowerCase() }}
        </p>
      </div>
    </template>

    <div class="flex flex-1 min-h-0">
      <nav class="w-[300px] shrink-0 bg-[#F0F0F4] border-r border-[#D8D8E2] flex flex-col">
        <button
          v-for="(label, index) in tabs"
          :key="index"
          :data-testid="tabTestIds[label]"
          @click="changeTab(index + 1)"
          class="flex items-center gap-4 px-4 py-4 text-left transition-colors duration-200"
          :class="
            tab === index + 1
              ? 'bg-[#2F7F91] text-white font-bold'
              : 'text-[#16161D] hover:bg-[#E8E8EE]'
          "
        >
          <v-icon
            size="24"
            :icon="tabIcons[label]"
            :color="tab === index + 1 ? 'white' : '#16161D'"
          />
          <span class="text-base leading-6 whitespace-nowrap">{{ label }}</span>
        </button>
      </nav>

      <div class="flex-1 flex flex-col min-w-0">
        <div class="flex-1 overflow-y-auto p-6">
          <h2 class="font-bold text-xl leading-6 text-[#323232] mb-6 text-left">
            {{ currentTabName }}
          </h2>
          <RestrictionPolicyTab
            v-if="tabs[tab - 1] === TAB_RESTRICTION_POLICY"
            ref="restrictionPolicyTabRef"
            :resource-type="props.resource.type"
            :resource-id="props.resource.id"
            :company-id="props.resource.companyId"
            @update:is-saving="isSaving = $event"
            @update:has-unsaved-changes="hasUnsavedChanges = $event"
          />
          <QualificationTransferPolicyTab
            v-if="tabs[tab - 1] === TAB_QUALIFICATION_TRANSFER"
            ref="qualificationTransferTabRef"
            :resource-type="props.resource.type"
            :resource-id="props.resource.id"
            :company-id="props.resource.companyId"
            @update:is-saving="isSaving = $event"
            @update:has-unsaved-changes="hasUnsavedChanges = $event"
          />
          <QualificationDocPolicyTab
            v-if="tabs[tab - 1] === TAB_QUALIFICATION_DOC"
            ref="qualificationDocTabRef"
            :resource-type="props.resource.type"
            :resource-id="props.resource.id"
            :company-id="props.resource.companyId"
            @update:is-saving="isSaving = $event"
            @update:has-unsaved-changes="hasUnsavedChanges = $event"
          />
        </div>

        <div class="shrink-0 px-6 pb-6">
          <div class="flex gap-6 w-full">
            <OutlinedButton
              data-testid="resource-policies-close-button"
              class="flex-1"
              text="Fechar"
              height="48px"
              @click="handleToggleModal"
            />
            <filled-button
              v-if="hasSaveAction"
              data-testid="resource-policies-save-button"
              class="flex-1"
              :disabled="saveDisabled"
              :loading="isSaving"
              color="primary30"
              :text="currentSaveLabel"
              height="48px"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </div>
  </modal-vue>

  <div v-if="showUnsavedAlert" class="unsaved-overlay" @click.self="cancelLeave">
    <div class="unsaved-card">
      <div class="unsaved-header">
        <p class="unsaved-title">Sair da aba {{ currentTabName }}?</p>
        <button class="unsaved-close" @click="cancelLeave">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 1L13 13M1 13L13 1"
              stroke="#323232"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <div class="unsaved-content">
        <p class="unsaved-message">
          Você tem edições não salvas na aba {{ currentTabName?.toLowerCase() }}. Se sair sem
          salvar, as alterações serão perdidas
        </p>
        <div class="unsaved-actions">
          <button class="unsaved-btn-voltar" @click="cancelLeave">Voltar</button>
          <button class="unsaved-btn-sair" @click="confirmLeave">Sair</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import {
  mdiEmailOpenMultipleOutline,
  mdiFileDocumentOutline,
  mdiFileDocumentCheckOutline
} from '@mdi/js'

import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { usePolicyLinkEditPermission } from '@/hooks/usePolicyLinkPermissions'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { AuthorizationResourcesAndSubjects } from '@/types/authorization'

import RestrictionPolicyTab from './tabs/RestrictionPolicyTab.vue'
import QualificationTransferPolicyTab from './tabs/QualificationTransferPolicyTab.vue'
import QualificationDocPolicyTab from './tabs/QualificationDocPolicyTab.vue'

const TAB_RESTRICTION_POLICY = 'Política de restrição'
const TAB_QUALIFICATION_TRANSFER = 'Qualificação para repasse'
const TAB_QUALIFICATION_DOC = 'Qualificação documental'

const props = defineProps<{
  resource: {
    type: AuthorizationResourcesAndSubjects
    id: number
    name: string
    companyId: number
  }
}>()

const modalName = EModals.RESOURCE_POLICIES_MODAL
const modalStore = useModalStore()
const { modals } = storeToRefs(modalStore)

const isSaving = ref(false)
const hasUnsavedChanges = ref(false)
const showUnsavedAlert = ref(false)
const pendingAction = ref<(() => void) | null>(null)

const restrictionPolicyTabRef = ref<InstanceType<typeof RestrictionPolicyTab> | null>(null)
const qualificationTransferTabRef = ref<InstanceType<typeof QualificationTransferPolicyTab> | null>(
  null
)
const qualificationDocTabRef = ref<InstanceType<typeof QualificationDocPolicyTab> | null>(null)

const tab = ref(1)

const { flags } = useFeatureFlags()

const tabs = computed(() => [
  TAB_RESTRICTION_POLICY,
  ...(flags.enabledPolicyModules ? [TAB_QUALIFICATION_TRANSFER, TAB_QUALIFICATION_DOC] : [])
])

const tabIcons: Record<string, string> = {
  [TAB_RESTRICTION_POLICY]: mdiFileDocumentOutline,
  [TAB_QUALIFICATION_TRANSFER]: mdiEmailOpenMultipleOutline,
  [TAB_QUALIFICATION_DOC]: mdiFileDocumentCheckOutline
}

const tabTestIds: Record<string, string> = {
  [TAB_RESTRICTION_POLICY]: 'resource-policies-tab-restriction-policy-button',
  [TAB_QUALIFICATION_TRANSFER]: 'resource-policies-tab-qualification-transfer-button',
  [TAB_QUALIFICATION_DOC]: 'resource-policies-tab-qualification-documental-button'
}

const currentTabName = computed(() => tabs.value[tab.value - 1])

const saveLabels: Record<string, string> = {
  [TAB_RESTRICTION_POLICY]: 'Salvar alterações',
  [TAB_QUALIFICATION_TRANSFER]: 'Salvar alterações',
  [TAB_QUALIFICATION_DOC]: 'Salvar alterações'
}

const currentSaveLabel = computed(() => saveLabels[currentTabName.value] || '')
const hasSaveAction = computed(() => !!currentSaveLabel.value)

const { canEditPolicyLinks } = usePolicyLinkEditPermission(() => props.resource.type)

// As tabs de qualificação (repasse e documental) não têm permissão própria —
// o gate de edição vale só para a tab de restrição
const saveDisabled = computed(() => {
  const permissionBlocked =
    currentTabName.value === TAB_RESTRICTION_POLICY && !canEditPolicyLinks.value
  return !hasUnsavedChanges.value || isSaving.value || permissionBlocked
})

const forceCloseModal = () => {
  showUnsavedAlert.value = false
  pendingAction.value = null
  modalStore.toggleModal(modalName)
  tab.value = 1
  isSaving.value = false
  hasUnsavedChanges.value = false
}

function guardedAction(action: () => void) {
  if (hasUnsavedChanges.value) {
    pendingAction.value = action
    showUnsavedAlert.value = true
  } else {
    action()
  }
}

const handleToggleModal = () => {
  guardedAction(forceCloseModal)
}

const changeTab = (index: number) => {
  if (index === tab.value) return
  guardedAction(() => {
    tab.value = index
    // A tab recém-montada não emite estado inicial — sem o reset, o estado da tab anterior vaza
    hasUnsavedChanges.value = false
    isSaving.value = false
  })
}

const confirmLeave = () => {
  showUnsavedAlert.value = false
  pendingAction.value?.()
  pendingAction.value = null
}

const cancelLeave = () => {
  showUnsavedAlert.value = false
  pendingAction.value = null
}

const handleSave = async () => {
  if (currentTabName.value === TAB_RESTRICTION_POLICY) {
    await restrictionPolicyTabRef.value?.handleSave()
  } else if (currentTabName.value === TAB_QUALIFICATION_TRANSFER) {
    await qualificationTransferTabRef.value?.handleSave()
  } else if (currentTabName.value === TAB_QUALIFICATION_DOC) {
    await qualificationDocTabRef.value?.handleSave()
  }
}
</script>

<style scoped>
.figma-custom-modal :deep(.modal-content) {
  border: 1px solid #d8d8e2 !important;
  border-radius: 8px !important;
  background-color: #ffffff !important;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08) !important;
  align-items: flex-start !important;
}
.figma-custom-modal :deep(main) {
  width: 100%;
  padding: 0 !important;
  overflow: hidden !important;
}

.unsaved-overlay {
  position: fixed;
  inset: 0;
  z-index: 210;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.48);
  backdrop-filter: blur(12px);
}

.unsaved-card {
  width: 525px;
  max-width: calc(100% - 32px);
  background: #ffffff;
  border: 1px solid #d8d8e2;
  border-radius: 8px;
  overflow: hidden;
  box-shadow:
    0px 4px 4px -1px rgba(12, 12, 13, 0.1),
    0px 4px 4px -1px rgba(12, 12, 13, 0.05);
}

.unsaved-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0 16px;
}

.unsaved-title {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  color: #323232;
  text-align: center;
  margin: 0;
}

.unsaved-close {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.unsaved-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  padding: 16px;
}

.unsaved-message {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #16161d;
  text-align: center;
  padding: 8px 0;
  margin: 0;
}

.unsaved-actions {
  display: flex;
  gap: 24px;
  align-items: center;
  width: 100%;
}

.unsaved-btn-voltar,
.unsaved-btn-sair {
  flex: 1;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.07px;
  cursor: pointer;
  white-space: nowrap;
}

.unsaved-btn-voltar {
  background: #2f7f91;
  color: #ffffff;
  border: none;
}

.unsaved-btn-sair {
  background: #ffffff;
  color: #da1e28;
  border: 2px solid #da1e28;
}
</style>
